import { defineStore } from 'pinia';
import axiosClient from '@/axiosClient';
import { API_ENDPOINTS } from '@/apiConfig';

let pendingCompaniesRequest = null;
let requestGeneration = 0;

export const useCompaniesStore = defineStore('companies', {
  state: () => ({
    companiesList: [],
    companiesLoading: false,
    companiesError: null,
    companiesLoaded: false,
  }),

  getters: {
    hasCompanies: (state) => state.companiesList.length > 0,
    companyById: (state) => (companyId) => (
      state.companiesList.find(company => company.company_id === companyId) || null
    ),
  },

  actions: {
    async fetchCompanies({ force = false } = {}) {
      if (!force && this.companiesLoaded) {
        return this.companiesList;
      }

      if (pendingCompaniesRequest) {
        return pendingCompaniesRequest;
      }

      const currentGeneration = requestGeneration;
      this.companiesLoading = true;
      this.companiesError = null;

      const request = (async () => {
        try {
          const url = API_ENDPOINTS.fetchAllCompanies
            .replace('{companyId}', 0)
            .replace('{archiveStatus}', 0);

          const response = await axiosClient.get(url);

          if (!Array.isArray(response.data)) {
            throw new Error('Invalid companies response: expected an array.');
          }

          const companies = response.data
            .map(company => ({
              company_email: company.company_email,
              company_id: company.company_id,
              company_joined: company.company_joined,
              company_lead_rate: company.company_lead_rate,
              company_name: company.company_name,
              company_name_short: company.company_name_short,
              company_spot_score: company.company_spot_score,
            }))
            .sort((companyA, companyB) => (
              companyA.company_name.localeCompare(companyB.company_name)
            ));

          if (currentGeneration === requestGeneration) {
            this.companiesList = companies;
            this.companiesLoaded = true;
          }

          return companies;
        } catch (error) {
          if (currentGeneration === requestGeneration) {
            this.companiesError = error.response?.data?.error
              || error.message
              || 'Failed to fetch companies.';
            this.companiesLoaded = false;
          }

          throw error;
        } finally {
          if (currentGeneration === requestGeneration) {
            this.companiesLoading = false;
          }
        }
      })();

      pendingCompaniesRequest = request;

      try {
        return await request;
      } finally {
        if (pendingCompaniesRequest === request) {
          pendingCompaniesRequest = null;
        }
      }
    },

    async ensureCompanies() {
      return this.fetchCompanies();
    },

    async refreshCompanies() {
      return this.fetchCompanies({ force: true });
    },

    clearCompanies() {
      requestGeneration += 1;
      pendingCompaniesRequest = null;
      this.$reset();
    },
  },
});
